import { clearSession, readSession, storeRenewedAccessToken } from '../auth/session';
import type { Problem } from './types';

/**
 * Where the API lives. Same origin everywhere: Vite proxies /balsam-hub in dev, the API
 * serves web/dist when SERVE_WEB is on, and on Cloudflare the Worker in web/worker
 * forwards /balsam-hub/* to the API host. Relative paths mean no CORS and no OPTIONS
 * preflight. VITE_API_ORIGIN remains for a build that must call a different host (the
 * API then needs CORS_ORIGIN, and every call costs a preflight).
 */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? '').replace(/\/$/, '');
export const API_BASE = `${API_ORIGIN}/balsam-hub/v1`;

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;

  constructor(status: number, problem: Problem | null, fallback?: string) {
    super(problem?.detail ?? problem?.title ?? fallback ?? `Request failed (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** The problem's machine-readable `code`, e.g. USER_NOT_CONFIRMED; null when the API sent none. */
  get code(): string | null {
    return this.problem?.code ?? null;
  }

  /** class-validator messages when the API answered 400 with `errors`. */
  get validationErrors(): string[] {
    const errors = this.problem?.errors;
    return Array.isArray(errors) ? errors.map(String) : [];
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Attach the bearer token (default). Public auth routes pass false. */
  auth?: boolean;
  /** Query parameters; undefined values are skipped. */
  query?: object;
  signal?: AbortSignal;
}

/** Problem `code` the API answers once the session has ended (docs/auth-sessions.md). */
export const SESSION_EXPIRED = 'SESSION_EXPIRED';

/** Where the API returns an access token it renewed for us. */
const ACCESS_TOKEN_HEADER = 'X-Access-Token';

/**
 * JSON request to the API. With `auth` (default) the stored access token is
 * attached as a bearer token — no expiry arithmetic, no refresh timer: an
 * access token lives five minutes and the API renews it server-side, handing
 * the replacement back in `X-Access-Token`, which is stored wherever it
 * appears.
 *
 * A 401 therefore means the session is really over — revoked, expired, or a
 * token the provider will not honour — so it clears the session and the app
 * falls back to the sign-in page. A 401 SESSION_EXPIRED clears it with that
 * reason, and the sign-in page says so.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = options.auth ?? true;
  const url = API_BASE + path + queryString(options.query);
  const token = auth ? (readSession()?.accessToken ?? null) : null;
  const response = await send(url, options, token);

  const renewed = response.headers.get(ACCESS_TOKEN_HEADER);
  if (renewed) storeRenewedAccessToken(renewed);

  if (response.status === 401 && auth && token) {
    clearSession((await isSessionExpiredProblem(response)) ? 'session-expired' : undefined);
  }
  return parse<T>(response);
}

/** Reads the problem code off a clone so the body stays available to parse(). */
async function isSessionExpiredProblem(response: Response): Promise<boolean> {
  try {
    const body: unknown = await response.clone().json();
    return isProblem(body) && body.code === SESSION_EXPIRED;
  } catch {
    return false;
  }
}

async function send(url: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }
  if (!response.ok) {
    throw new ApiError(response.status, isProblem(body) ? body : null, text || undefined);
  }
  return body as T;
}

function isProblem(body: unknown): body is Problem {
  return !!body && typeof body === 'object' && 'status' in body && 'title' in body;
}

function queryString(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Human-readable message for any error a page catches. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const details = error.validationErrors;
    if (details.length) return details.join('. ');
    if (error.status === 429) return 'Too many attempts. Wait a minute and try again.';
    if (error.status === 503 && !error.problem?.detail)
      return 'The service is temporarily unavailable.';
    return error.message;
  }
  if (error instanceof TypeError) return 'Could not reach the API. Check your connection.';
  return error instanceof Error ? error.message : String(error);
}
